import { Component, forwardRef, input, signal, computed } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { LucideIconComponent } from '../../lucide-icon/lucide-icon.component';
import { TextComponent } from '../../typography/text/text.component';
import { CheckOverflowDirective } from '../../../directives/check-overflow.directive';

// Define a clear interface for the options
export interface RadioOption {
  value: any;
  label: string;
  description?: string;
  icon?: string;
}

@Component({
  selector: 'app-radio-card-group',
  standalone: true,
  imports: [
    LucideIconComponent,
    TextComponent,
    CheckOverflowDirective
],
  templateUrl: './radio-card-group.component.html',
  styleUrls: ['./radio-card-group.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioCardGroupComponent),
      multi: true,
    },
  ],
})
export class RadioCardGroupComponent implements ControlValueAccessor {
  // --- Inputs ---
  options = input.required<RadioOption[]>();
  layout = input<'list' | 'grid'>('grid');
  gridCols = input<number | null>(null); // Changed to allow null for auto-responsive

  // --- State ---
  value = signal<any>(null);
  isDisabled = signal(false);
  expandedState = signal<Record<string, boolean>>({}); // For expandable list items
  isOverflowing = signal<Record<string, boolean>>({}); // New state for overflow

  // --- Computed Properties ---
  readonly containerClasses = computed(() => {
    const base = 'radio-group-container';
    if (this.layout() === 'grid') {
      // If gridCols is NOT provided, we add a class for the auto-responsive grid
      return `${base} grid gap-4 ${
        !this.gridCols() ? 'auto-responsive-grid' : ''
      }`;
    }
    return `${base} flex flex-col gap-3`;
  });

  readonly gridStyle = computed(() => {
    // Only apply this style if gridCols IS provided
    if (this.layout() === 'grid' && this.gridCols()) {
      return { 'grid-template-columns': `repeat(${this.gridCols()}, 1fr)` };
    }
    return {};
  });

  // --- ControlValueAccessor ---
  onChange = (_value: unknown) => {};
  onTouched = () => {};

  writeValue(value: unknown): void {
    this.value.set(value);
  }
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  // --- Methods ---
  selectOption(value: any): void {
    if (this.isDisabled()) return;
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  toggleExpand(event: MouseEvent, value: any): void {
    event.stopPropagation(); // Prevent the card from being selected
    this.expandedState.update((current) => ({
      ...current,
      [value]: !current[value],
    }));
  }

  // New method to handle the directive's output
  onOverflowChange(isOverflowing: boolean, value: any): void {
    this.isOverflowing.update((current) => ({
      ...current,
      [value]: isOverflowing,
    }));
  }
}
